import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Clock, Send, Mail, UserCheck, ShieldOff, TrendingUp } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { format } from 'date-fns';
import { api } from '../../lib/api';
import { LoadingState, EmptyState, ErrorState, Card } from '../../components/control-center/StateWrappers';
import { StatusBadge } from '../../components/control-center/Layout';
import { Button } from '../../components/ui/button';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return format(new Date(iso), 'dd/MM/yyyy HH:mm'); } catch { return '—'; }
};
const fmtSeconds = (s) => (s === null || s === undefined ? '—' : `${Number(s).toFixed(1)} s`);

const Metric = ({ label, value, icon: Icon, testid }) => (
  <Card className="p-5">
    <div className="flex items-center gap-3 text-slate-500 text-xs font-medium uppercase tracking-wide mb-3">
      {Icon && <Icon className="w-4 h-4" />} <span>{label}</span>
    </div>
    <p className="text-3xl font-bold text-slate-900" data-testid={testid}>
      {value ?? '—'}
    </p>
  </Card>
);

const Dashboard = () => {
  const [state, setState] = useState({ loading: true, error: false, data: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: false }));
    try {
      const { data } = await api.get('/control-center/overview');
      setState({ loading: false, error: false, data });
    } catch {
      setState({ loading: false, error: true, data: null });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Resumen operativo de tu hotel.</p>
        </div>
        <Button onClick={load} data-testid="dashboard-refresh" className="bg-white text-slate-800 border border-slate-200 hover:bg-slate-50 gap-2">
          <RefreshCw className={`w-4 h-4 ${state.loading ? 'animate-spin' : ''}`} /> Actualizar
        </Button>
      </div>

      {state.loading && <LoadingState />}
      {state.error && !state.loading && <ErrorState onRetry={load} />}
      {!state.loading && !state.error && state.data && (
        <DashboardContent data={state.data} />
      )}
    </div>
  );
};

const DashboardContent = ({ data }) => {
  const today = data.today || {};
  const l30 = data.last_30_days || {};
  const hotel = data.hotel || {};
  const daily = Array.isArray(data.daily) ? data.daily : [];

  return (
    <div className="space-y-6">
      {/* Status header */}
      <Card className="p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Estado</p>
          <div className="flex items-center gap-3">
            <StatusBadge active={hotel.automation_active} />
          </div>
        </div>
        <div className="text-sm text-slate-600 grid grid-cols-2 sm:flex sm:items-center gap-x-8 gap-y-2">
          <div><span className="block text-xs text-slate-500">Última sincronización</span><span className="font-medium text-slate-900">{fmtDate(hotel.last_sync_at)}</span></div>
          <div><span className="block text-xs text-slate-500">Último correo</span><span className="font-medium text-slate-900">{fmtDate(hotel.last_email_processed_at)}</span></div>
          <div><span className="block text-xs text-slate-500">T. medio</span><span className="font-medium text-slate-900">{fmtSeconds(today.average_processing_seconds)}</span></div>
        </div>
      </Card>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric label="Procesados hoy"       value={today.processed}          icon={Send}      testid="metric-processed" />
        <Metric label="Borradores hoy"       value={today.drafts}             icon={Mail}      testid="metric-drafts" />
        <Metric label="Revisión humana"      value={today.human_review}       icon={UserCheck} testid="metric-review" />
        <Metric label="Sin respuesta"        value={today.no_reply_needed}    icon={ShieldOff} testid="metric-noreply" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Metric label="Correos últimos 30 días" value={l30.emails ?? '—'} icon={TrendingUp} testid="metric-30d" />
        <Card className="p-5 md:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <p className="text-slate-900 font-semibold">Volumen diario</p>
            <span className="text-xs text-slate-500">Últimos {daily.length} días</span>
          </div>
          {daily.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-500">Sin datos suficientes para mostrar la gráfica.</div>
          ) : (
            <div style={{ width: '100%', height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" fontSize={11} stroke="#94a3b8" tickFormatter={(v)=>{ try{return format(new Date(v),'dd/MM');}catch{return v;} }} />
                  <YAxis fontSize={11} stroke="#94a3b8" allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="emails" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
